import React, { useState, useEffect } from 'react';
import './MapLayers.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSatelliteDish,
  faRoad,
  faCloudRain,
  faCloud,
  faSnowflake,
  faTemperatureHalf,
  faTemperatureHigh,
  faDroplet,
  faWind,
  faSun,
  faLocationDot,
  faBolt,
  faTornado,
  faWater,
  faSnowplow,
  faShip,
  faHurricane,
  faTriangleExclamation,
  faFire,
  faEarthAmericas,
  faMapLocation,
  faCloudShowersHeavy
} from '@fortawesome/free-solid-svg-icons';
import { faFireFlameCurved } from '@fortawesome/free-solid-svg-icons/faFireFlameCurved';

const LayerIcon = ({ type }) => {
  if (!type) {
    console.log('Type is undefined');
    return <FontAwesomeIcon icon={faLocationDot} />;
  }

  // Map of layer types to icons
  const iconMap = {
    // Base layers
    satellite: faSatelliteDish,
    roads: faRoad,
    
    // Weather layers
    radar: faCloudRain,
    'radar+sat': faCloud,
    radar_satellite: faCloud,
    precipitation: faCloudRain,
    precip: faCloudShowersHeavy,
    snow: faSnowflake,
    temp: faTemperatureHalf,
    temp_feels_like: faTemperatureHigh,
    feels_like: faTemperatureHigh,
    dew_point: faDroplet,
    wind: faWind,
    wind_speed: faWind,
    wind_speed_gusts: faWind,
    uv: faSun,
    uv_index: faSun,
    
    // Overlays
    esri_roads: faRoad,
    esri_labels: faMapLocation,
    storm_tracks: faBolt,
    current_conditions: faSun,
    temp_plots: faTemperatureHalf,
    
    // Alerts
    tornado: faTornado,
    tornado_severe_weather: faTornado,
    flood: faWater,
    winter: faSnowflake,
    marine: faShip,
    tropical: faHurricane,
    hurricane: faHurricane,
    other: faTriangleExclamation,
    other_alerts: faTriangleExclamation,
    wildfire: faFireFlameCurved,
    wildfire_detection: faFireFlameCurved,
    earthquakes: faEarthAmericas,
    lightning: faBolt
  };

  // Clean up the type and remove base/overlay prefixes
  const cleanType = type.toLowerCase().replace(/^(base_|overlay_)/, '');
  
  // Try to find the icon in our map
  const icon = iconMap[cleanType];
  if (icon) {
    return <FontAwesomeIcon icon={icon} />;
  }

  // If no direct match, try to find a partial match
  const partialMatch = Object.keys(iconMap).find(key => cleanType.includes(key));
  if (partialMatch) {
    return <FontAwesomeIcon icon={iconMap[partialMatch]} />;
  }

  console.log('Unknown icon type:', type, 'cleaned to:', cleanType);
  return <FontAwesomeIcon icon={faLocationDot} />;
};

const MapLayers = ({ isOpen, onClose, onLayerSelect, activeLayer, activeBaseLayer, zipcode = '59911' }) => {
  const [selectedOverlays, setSelectedOverlays] = useState(['overlay_esri_roads', 'overlay_esri_labels']);
  const [opacity, setOpacity] = useState(0.75); // Default opacity of 75%
  
  // Get API key from WordPress settings
  const apiKey = window?.wpApiSettings?.weatherApiKey || ''; // Get from WordPress
  
  // Get current timestamp for layers
  const currentTimestamp = Math.floor(Date.now() / 1000); // Convert from milliseconds to seconds

  
  
  // Set up event listeners to prevent map interaction while scrolling
  useEffect(() => {
    const handlePanelInteraction = (e) => {
      // Stop propagation to prevent map from handling these events
      e.stopPropagation();
    };
    
    const panelContent = document.querySelector('.layers-panel-content');
    if (panelContent) {
      const events = ['wheel', 'mousewheel', 'DOMMouseScroll', 'touchstart', 'touchmove'];
      
      events.forEach(event => {
        panelContent.addEventListener(event, handlePanelInteraction, { passive: false });
      });
      
      return () => {
        events.forEach(event => {
          panelContent.removeEventListener(event, handlePanelInteraction);
        });
      };
    }
  }, [isOpen]);
  
  // Base layers (only one can be active at a time)
  const baseLayers = [
    { id: 'base_satellite', label: 'Satellite', url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}' },
    { id: 'base_roads', label: 'Roads', url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png' }
  ];
  const weatherLayers = [
    { id: 'radar', label: 'Radar', product: 'radarFcst' },
    { id: 'radar_satellite', label: 'Radar+Sat', product: 'satradFcst' },
    { id: 'satellite', label: 'Satellite', product: 'cloudsFcst' },
    { id: 'precipitation', label: 'Precip', product: 'precip24hrfcts' },
    { id: 'snow', label: 'Snow', product: 'snow24hr' },
    { id: 'temp', label: 'Temp', product: 'tempFcst' },
    { id: 'temp_feels_like', label: 'Feels Like', product: 'feelsLikeFcst' },
    { id: 'dew_point', label: 'Dew Point', product: 'dewpointFcst' },
    { id: 'wind_speed_gusts', label: 'Wind', product: 'windSpeedFcst' },
    { id: 'uv_index', label: 'UV Index', product: 'uvFcst' }
  ];
  // Weather layers (only one can be active at a time)
  const weatherLayers_past = [
    { id: 'radar', label: 'Radar', product: 'radar' },
    { id: 'radar_satellite', label: 'Radar+Sat', product: 'satrad' },
    { id: 'satellite', label: 'Satellite', product: 'sat' },
    { id: 'precipitation', label: 'Precip', product: 'precip24hr' },
    { id: 'snow', label: 'Snow', product: 'snow24hr' },
    { id: 'temp', label: 'Temp', product: 'temp' },
    { id: 'temp_feels_like', label: 'Feels Like', product: 'feelsLike' },
    { id: 'dew_point', label: 'Dew Point', product: 'dewpoint' },
    { id: 'wind_speed_gusts', label: 'Wind', product: 'windSpeed' },
    { id: 'uv_index', label: 'UV Index', product: 'uv' }
  ];
  
  // Overlays (multiple can be active at once)
  const overlays = [
    { id: 'overlay_esri_roads', label: 'Roads & Highways', url: 'https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}' },
    { id: 'overlay_esri_labels', label: 'Places & Boundaries', url: 'https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}' },
    { id: 'overlay_storm_tracks', label: 'Storm Tracks', url: `/wp-json/oht-weather/v1/vector-warnings?zipcode=${zipcode}&product=790` },
    { id: 'overlay_current_conditions', label: 'Current Conditions', url: `/wp-json/oht-weather/v1/vector-warnings?zipcode=${zipcode}&product=613` },
    { id: 'overlay_temp_plots', label: 'Temp Plots', url: `/wp-json/oht-weather/v1/vector-warnings?zipcode=${zipcode}&product=613` },
    { id: 'overlay_earthquakes', label: 'Earthquakes', url: `/wp-json/oht-weather/v1/vector-warnings?zipcode=${zipcode}&product=601` },
    { id: 'overlay_wildfire_detection', label: 'Wildfires', url: `/wp-json/oht-weather/v1/vector-warnings?zipcode=${zipcode}&product=603` },
    { id: 'overlay_lightning', label: 'Lightning', url: `/wp-json/oht-weather/v1/vector-warnings?zipcode=${zipcode}&product=408` }
  ];
  
  // Alert overlays (multiple can be active at once)
  const alertOverlays = [
    { id: 'overlay_tornado_severe_weather', label: 'Tornado', url: `/wp-json/oht-weather/v1/vector-warnings?zipcode=${zipcode}&product=608` },
    { id: 'overlay_flood', label: 'Flood', url: `/wp-json/oht-weather/v1/vector-warnings?zipcode=${zipcode}&product=609` },
    { id: 'overlay_winter', label: 'Winter', url: `/wp-json/oht-weather/v1/vector-warnings?zipcode=${zipcode}&product=610` },
    { id: 'overlay_marine', label: 'Marine', url: `/wp-json/oht-weather/v1/vector-warnings?zipcode=${zipcode}&product=607` },
    { id: 'overlay_tropical', label: 'Tropical', url: `/wp-json/oht-weather/v1/vector-warnings?zipcode=${zipcode}&product=612` },
    { id: 'overlay_other_alerts', label: 'Other', url: `/wp-json/oht-weather/v1/vector-warnings?zipcode=${zipcode}&product=611` }
  ];
  
  // Handler for selecting a base layer
  const handleBaseLayerSelect = (layer) => {
    console.log(`Selected base layer: ${layer.id} with URL: ${layer.url}`);
    onLayerSelect(layer.id, layer.url);
  };
  
  // Handler for selecting a weather layer
  const handleWeatherLayerSelect = (layer) => {
  
    
    const product = layer.product;
    console.log(`Selected weather layer: ${layer.id} with product: ${product}`);
    
    // Construct the URL using the product and timestamp
    const url = `https://api.weather.com/v3/TileServer/tile?product=${product}&ts=${currentTimestamp}&xyz={x}:{y}:{z}&apiKey=${apiKey}`;
    
    onLayerSelect(layer.id, url);
  };
  
  // Handler for toggling an overlay
  const handleOverlayToggle = (overlay) => {
    // Use the full overlay ID for tracking
    const overlayId = overlay.id;
    const isSelected = selectedOverlays.includes(overlayId);
    console.log(`${isSelected ? 'Removing' : 'Adding'} overlay: ${overlay.id}`);
    
    if (isSelected) {
      // Remove overlay
      setSelectedOverlays(prev => prev.filter(id => id !== overlayId));
      // Call parent handler to remove the overlay
      onLayerSelect(overlay.id, null);
    } else {
      // Add overlay
      setSelectedOverlays(prev => [...prev, overlayId]);
      // Call parent handler to add the overlay
      onLayerSelect(overlay.id, overlay.url);
    }
  };
  
  // Stop propagation of events to prevent map interaction
  const handleMouseEvent = (e) => {
    e.stopPropagation();
  };
  
  // Handler for opacity changes
  const handleOpacityChange = (e) => {
    const newOpacity = parseFloat(e.target.value);
    setOpacity(newOpacity);
    onLayerSelect('opacity', newOpacity);
  };
  
  if (!isOpen) return null;
  
  return (
    <div 
      className="layers-panel"
      onClick={handleMouseEvent}
      onMouseDown={handleMouseEvent}
      onTouchStart={handleMouseEvent}
    >
      <div className="layers-panel-header">
        <button className="close-btn" onClick={onClose}>×</button>
      </div>
      
      <div className="layers-panel-content">
        <div className="layer-section">
          <h4>Weather Layer Opacity</h4>
          <div className="opacity-control">
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={opacity}
              onChange={handleOpacityChange}
              className="opacity-slider"
            />
            <span className="opacity-value">{Math.round(opacity * 100)}%</span>
          </div>
        </div>

        <div className="layer-section">
          <h4>Base Map</h4>
          <ul className="layer-list">
            {baseLayers.map(layer => (
              <li 
                key={layer.id}
                className={`layer-item ${activeBaseLayer === layer.id ? 'active' : ''}`}
                onClick={() => handleBaseLayerSelect(layer)}
              >
                <div className="layer-icon">
                  <LayerIcon type={layer.id} />
                </div>
                <span className="layer-text">{layer.label}</span>
              </li>
            ))}
          </ul>
        </div>
        
        <div className="layer-section">
          <h4>Weather Layers</h4>
          <ul className="layer-list">
            {weatherLayers.map(layer => (
              <li 
                key={layer.id}
                className={`layer-item ${activeLayer === layer.id ? 'active' : ''}`}
                onClick={() => handleWeatherLayerSelect(layer)}
              >
                <div className="layer-icon">
                  <LayerIcon type={layer.id} />
                </div>
                <span className="layer-text">{layer.label}</span>
              </li>
            ))}
          </ul>
        </div>
        
        {/* <div className="layer-section">
          <h4>Weather Overlays</h4>
          <ul className="layer-list">
            {overlays.map(overlay => (
              <li key={overlay.id} className="layer-item">
                <label>
                  <div className="layer-icon">
                    <LayerIcon type={overlay.id} />
                  </div>
                  <input 
                    type="checkbox"
                    checked={selectedOverlays.includes(overlay.id)}
                    onChange={() => handleOverlayToggle(overlay)}
                  />
                  <span className="layer-text">{overlay.label}</span>
                </label>
              </li>
            ))}
          </ul>
        </div> */}
        
        <div className="layer-section">
          <h4>Weather Alerts</h4>
          <ul className="layer-list">
            {alertOverlays.map(overlay => (
              <li key={overlay.id} className="layer-item">
                <label>
                  <div className="layer-icon">
                    <LayerIcon type={overlay.id} />
                  </div>
                  <input 
                    type="checkbox"
                    checked={selectedOverlays.includes(overlay.id)}
                    onChange={() => handleOverlayToggle(overlay)}
                  />
                  <span className="layer-text">{overlay.label}</span>
                </label>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default MapLayers;